






































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject, Watch, ProvideReactive } from 'vue-property-decorator';

// Shared types
import {
    Brand,
    EnergyLabel,
    FilterTechnology,
    IndoorUnitLocation,
    OutdoorUnit,
    ProductLine,
    System,
    Texts,
} from '@coldtec/cos-types';

// Store types
import { ToolSystem, CompanyGetter } from '@/store/store.types';

// Button types
import { ButtonOptions, ButtonType } from '@/components/input/types';

// Modal
import Modal from '@/components/modal/Modal.vue';

// Holding the summary of the brand, product line and SKU
import SystemSummary from './system/SystemSummary.vue';

/**
 *  Holding the details of a specification to render.
 */
interface Specification {
    /** The title of the specification (left). */
    title: string;

    /** The value of the specification (right). */
    value: string | number;

    /**
     *
     *  The value of the interrogation tooltip if it should be added.
     *
     *  (i) It uses the title as label
     *
     */
    tooltip?:string;
}

/**
 *  Component for rendering the specifications modal of a system,
 *  including a way to toggle the selection of the system.
 */
@Component({
    components: {
        SystemSummary,
    },
})
export default class SystemsSpecsModal extends Vue {

    public $refs!: {
        modal: Modal;
    };

    /**
     *  Used to get all the texts of the company.
     */
    private get companyTexts(): Texts {

        return this.$store.getters[CompanyGetter.TEXTS];

    }

    /**
     *
     *  Provided by the `Systems.vue` file for toggling the selected
     *  state of the system (for the currently showing space(s)).
     *
     *  @param systemGuid The guid of the system to toggle the selected state of
     *
     */
    @Inject('toggleSystem')
    private toggleSystem!: (systemGuid: string) => void;

    /**
     *  Provide both the system and tool system to the `SystemSummary.vue` component
     */
    @ProvideReactive('system')
    private system: System | null = null;

    @ProvideReactive('toolSystem')
    private toolSystem: ToolSystem | null = null;

    /**
     *
     *  Holding the Array of specifications to render.
     *
     *  (i) Constructed in the `_constructSpecs()` method
     *
     */
    private specs: Array<Specification> = [];

    /**
     *
     *  Holding the details/state of the toggle button.
     *
     *  (i) His state is updated in the `_selectedUpdate()` respectively
     *
     */
    private toggleButton: ButtonOptions | null = null;

    /**
     *  Used to open the specifications modal for the forwarded system guid.
     */
    public open(system: System, toolSystem: ToolSystem): void {

        this.system = system;
        this.toolSystem = toolSystem;

        this._constructSpecs();

        this.$refs.modal.open();

    }

    /**
     *  Used to close the specifications modal.
     */
    public close(): void {

        this.$refs.modal.close();

    }

    /**
     *  Used to toggle the selected state of the system
     *  for which the specs are currently shown.
     */
    private toggle(): void {

        if (!this.system) {

            return;

        }

        this.toggleSystem(this.system.guid);

    }

    /**
     *
     *  Used to watch the selected state of the (tool) system,
     *  update the toggle button respectively and close
     *  the modal in case it got selected.
     *
     *  (i) We're closing the modal so the user gets to see the
     *  highlighted units (multi) or accessories (single)
     *
     */
    @Watch('toolSystem.selected', { immediate: true })
    private _selectedUpdate(selected: boolean): void {

        if (!this.system) {

            return;

        }

        /**
         *  Update the toggle button respectively
         */
        this.toggleButton = {
            type: selected ? ButtonType.Warning : ButtonType.Success,
            title: `${ selected ? 'Deselecteer' : 'Selecteer' } de ${ (this.system.brand as Brand).name } ${ this.system.sku } `,
            content: selected ? 'Deselecteer' : 'Selecteer',
        };

        /**
         *
         *  Close the modal so the user gets to see
         *  either the units or accessories.
         *
         *  (i) To which is scrolled and highlight by the `System.vue`
         *  and `SystemAccordion.vue` components.
         *
         */
        if (selected) {

            this.close();

        }

    }

    /**
     *  Used to construct the specifications Array that is used for painting the specs.
     */
    private _constructSpecs(): void {

        if (!this.system) {

            return;

        }

        /**
         *  Construct the default specifications.
         */
        this.specs = [
            {
                title: 'Merk',
                value: (this.system.brand as Brand).name,
            },
            {
                title: 'Model',
                value: this.system.sku,
            },
            {
                title: 'Product lijn',
                value: (this.system.product_line as ProductLine).name,
            },
            {
                title: 'Montagelocatie binnenunit',
                value: (this.system.indoor_unit_location as IndoorUnitLocation).name,
            },
            {
                title: 'Koelcapaciteit',
                value: `${ this.system.maximum_cooling_capacity } kW`,
            },
            {
                title: 'Verwarmingscapaciteit',
                value: `${ this.system.maximum_heating_capacity } kW`,
            },
            {
                title: 'Energielabel koelen',
                value: (this.system.cooling_energy_label as EnergyLabel).name,
            },
            {
                title: 'Energielabel verwarmen',
                value: (this.system.heating_energy_label as EnergyLabel).name,
            },
            {
                title: this.companyTexts.help_filter_technology_title,
                value: (this.system.filter_technology as FilterTechnology).name,
                tooltip: this.companyTexts.help_filter_technology_tooltip,
            },
            {
                title: this.companyTexts.help_wifi_title,
                value: this.system.wifi ? 'Ja' : 'Nee',
                tooltip: this.companyTexts.help_wifi_tooltip,
            },
        ];

        /**
         *
         *  Get the outdoor unit of the system and add his refrigerant details.
         *
         *  (!) A system currently always has a single outdoor unit
         *
         */
        const outdoorUnit: OutdoorUnit = (this.system.outdoor_units as OutdoorUnit[])[0];

        if (!outdoorUnit) {

            return;

        }

        this.specs.push({
            title: 'Koudemiddel',
            value: 'Dit product of apparatuur bevat gefluoreerde broeikasgassen',
        });

        this.specs.push({
            title: 'Type koudemiddel (GWP)',
            value: `${ outdoorUnit.refrigerant_type } (${ outdoorUnit.refrigerant_gwp })`,
        });

        this.specs.push({
            title: 'Koudemiddelinhoud',
            value: `${ outdoorUnit.refrigerant_content } kg`,
        });

        /**
         *
         *  Compute the CO2 equivalent of the system's F gas.
         *
         *  (i) The amount of gas is stored as kg (`refrigerant_content`)
         *
         *  https://www.gov.uk/guidance/calculate-the-carbon-dioxide-equivalent-quantity-of-an-f-gas
         *
         */
        const equivalent = (outdoorUnit.refrigerant_content / 1000) * outdoorUnit.refrigerant_gwp;

        this.specs.push({
            title: 'CO2 equivalent',
            value: `${ equivalent } ton`,
        });

    }

}
